import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import { Link as RouterLink } from 'react-router-dom';
import dayjs from 'dayjs';
import { formatGuidePrice } from '@/features/guide/utils/pricing';
import { PRICING_TYPE_LABELS } from '@/features/booking/utils/bookingPricing';
import { bookingStatusToBadge, formatStatusLabel } from '@/shared/utils/statusBadge';
import { formatBookingDuration, formatBookingWhen } from '@/shared/utils/bookingWhen';
import { isExtendableStatus } from '@/features/trip-extension/utils/extensionDisplay';
import type { StatusBadgeVariant } from '@/shared/components/ui/StatusBadge';
import { ContactLockedNotice } from '@/features/booking/components/ContactLockedNotice';
import { BookingStatus } from '@/types';
import type { Booking } from '@/types';

const STATUS_CHIP_COLOR: Record<StatusBadgeVariant, { bg: string; color: string }> = {
  approved: { bg: '#DCFCE7', color: '#047857' },
  pending: { bg: '#FEF3C7', color: '#B45309' },
  under_review: { bg: '#DBEAFE', color: '#1D4ED8' },
  rejected: { bg: '#FEE2E2', color: '#B91C1C' },
  suspended: { bg: '#F1F5F9', color: '#475569' },
  profile_incomplete: { bg: '#F1F5F9', color: '#475569' },
};

interface TravelerBookingCardProps {
  booking: Booking;
  onPay?: (bookingId: string) => void;
  onCancel?: (bookingId: string) => void;
  onConfirmCompletion?: (bookingId: string) => void;
  onDispute?: (booking: Booking) => void;
  onReview?: (booking: Booking) => void;
  onSos?: (booking: Booking) => void;
  payPending?: boolean;
  cancelPending?: boolean;
  confirmPending?: boolean;
}

function capitalizeLabel(value: string) {
  if (!value) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function initialsFromName(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

export function TravelerBookingCard({
  booking,
  onPay,
  onCancel,
  onConfirmCompletion,
  onDispute,
  onReview,
  onSos,
  payPending,
  cancelPending,
  confirmPending,
}: TravelerBookingCardProps) {
  const guideName = booking.guide?.fullName ?? 'Guide booking';
  const destination = capitalizeLabel(
    booking.destinationLabel ?? booking.destinationName ?? 'Trip'
  );
  const formattedDate = formatBookingWhen(booking);
  const statusKey = bookingStatusToBadge(booking.status);
  const statusStyle = STATUS_CHIP_COLOR[statusKey];
  const photo = booking.guide?.profilePhoto;

  const holdActive =
    booking.status === BookingStatus.PAYMENT_PENDING &&
    Boolean(booking.holdExpiresAt) &&
    dayjs(booking.holdExpiresAt).isAfter(dayjs());

  const showAlert =
    holdActive ||
    booking.status === BookingStatus.EXPIRED ||
    booking.status === BookingStatus.UPCOMING ||
    booking.status === BookingStatus.IN_PROGRESS ||
    booking.status === BookingStatus.WAITING_FOR_TRAVELER_CONFIRMATION ||
    booking.status === BookingStatus.DISPUTED;

  const guideContacts = booking.guide;
  const hasGuideContacts = Boolean(
    guideContacts?.phone || guideContacts?.whatsappNumber || guideContacts?.email
  );
  const contactsLocked = booking.contactAccess?.state === 'locked';

  const alertMessage = (() => {
    switch (booking.status) {
      case BookingStatus.PAYMENT_PENDING:
        return `Complete payment by ${dayjs(booking.holdExpiresAt).format('h:mm A')} or this time slot is released.`;
      case BookingStatus.EXPIRED:
        return "Payment wasn't completed in time, so this slot was released. You can book again.";
      case BookingStatus.UPCOMING:
        return `Trip starts on ${formattedDate}.`;
      case BookingStatus.IN_PROGRESS:
        return 'Your trip is in progress.';
      case BookingStatus.WAITING_FOR_TRAVELER_CONFIRMATION:
        return `Guide marked complete — please confirm${
          booking.travelerConfirmDeadline
            ? ` by ${dayjs(booking.travelerConfirmDeadline).format('D MMM')}`
            : ''
        }.`;
      case BookingStatus.DISPUTED:
        return 'Under dispute review.';
      default:
        return '';
    }
  })();

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        p: { xs: 2, sm: 2.5 },
        transition: 'box-shadow 200ms ease, border-color 200ms ease',
        '&:hover': {
          borderColor: 'primary.100',
          boxShadow: '0 10px 28px rgba(15, 23, 42, 0.08)',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 2, sm: 2.5 },
          alignItems: { sm: 'flex-start' },
        }}
      >
        <Avatar
          src={photo || undefined}
          alt={guideName}
          sx={{
            width: { xs: 64, sm: 72 },
            height: { xs: 64, sm: 72 },
            flexShrink: 0,
            bgcolor: 'primary.main',
            fontWeight: 700,
            fontSize: '1.25rem',
          }}
        >
          {initialsFromName(guideName) || 'G'}
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 1.5,
              mb: 1,
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography
                  sx={{
                    fontSize: { xs: '1.0625rem', sm: '1.1875rem' },
                    fontWeight: 700,
                    color: 'primary.dark',
                    lineHeight: 1.3,
                    fontFamily: '"Playfair Display", Georgia, serif',
                  }}
                >
                  {guideName}
                </Typography>
                <Chip
                  label={formatStatusLabel(booking.status)}
                  size="small"
                  sx={{
                    bgcolor: statusStyle.bg,
                    color: statusStyle.color,
                    fontWeight: 700,
                    fontSize: '0.6875rem',
                    height: 24,
                    textTransform: 'capitalize',
                  }}
                />
              </Stack>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75 }}>
                <PlaceOutlinedIcon sx={{ fontSize: 15, color: 'primary.main' }} />
                <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary', fontWeight: 500 }}>
                  {destination}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ textAlign: { xs: 'left', sm: 'right' }, flexShrink: 0 }}>
              <Typography
                sx={{
                  fontSize: { xs: '1.25rem', sm: '1.375rem' },
                  fontWeight: 700,
                  color: 'text.primary',
                  lineHeight: 1.2,
                }}
              >
                {formatGuidePrice(booking.grossAmount, booking.currency)}
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.25 }}>
                Total amount
              </Typography>
            </Box>
          </Box>

          <Stack
            direction="row"
            flexWrap="wrap"
            useFlexGap
            spacing={1}
            sx={{ mb: showAlert || hasGuideContacts ? 1.75 : 0 }}
          >
            <MetaChip
              icon={<CalendarMonthOutlinedIcon sx={{ fontSize: '15px !important' }} />}
              label={formattedDate}
            />
            <MetaChip
              icon={<AccessTimeOutlinedIcon sx={{ fontSize: '15px !important' }} />}
              label={formatBookingDuration(booking)}
            />
            {(booking.extendedHours ?? 0) > 0 && (
              <MetaChip
                icon={<AccessTimeOutlinedIcon sx={{ fontSize: '15px !important' }} />}
                label={`+${booking.extendedHours} h extra time`}
              />
            )}
            <MetaChip
              icon={<GroupsOutlinedIcon sx={{ fontSize: '15px !important' }} />}
              label={`${booking.travelerCount} traveler${booking.travelerCount > 1 ? 's' : ''}`}
            />
            <Chip
              label={PRICING_TYPE_LABELS[booking.pricingType]}
              size="small"
              sx={{
                height: 28,
                fontSize: '0.75rem',
                fontWeight: 600,
                bgcolor: 'primary.50',
                color: 'primary.dark',
              }}
            />
          </Stack>

          {showAlert && (
            <Alert
              severity={
                booking.status === BookingStatus.WAITING_FOR_TRAVELER_CONFIRMATION ||
                booking.status === BookingStatus.DISPUTED ||
                booking.status === BookingStatus.PAYMENT_PENDING
                  ? 'warning'
                  : 'info'
              }
              sx={{ mb: 1.75, py: 0.25, fontSize: '0.8125rem', borderRadius: 2 }}
            >
              {alertMessage}
            </Alert>
          )}

          {contactsLocked && (
            <ContactLockedNotice audience="traveler" unlocksAt={booking.contactAccess?.unlocksAt} compact />
          )}

          {hasGuideContacts && guideContacts && (
            <Stack
              direction="row"
              flexWrap="wrap"
              useFlexGap
              spacing={1}
              sx={{ mb: 0.5 }}
            >
              {guideContacts.phone && (
                <ContactChip
                  icon={<PhoneOutlinedIcon sx={{ fontSize: '14px !important' }} />}
                  label={guideContacts.phone}
                  href={`tel:${guideContacts.phone}`}
                />
              )}
              {guideContacts.whatsappNumber && (
                <ContactChip
                  icon={<PhoneOutlinedIcon sx={{ fontSize: '14px !important' }} />}
                  label={`WhatsApp ${guideContacts.whatsappNumber}`}
                  href={`https://wa.me/${guideContacts.whatsappNumber.replace(/\D/g, '')}`}
                  external
                />
              )}
              {guideContacts.email && (
                <ContactChip
                  icon={<EmailOutlinedIcon sx={{ fontSize: '14px !important' }} />}
                  label={guideContacts.email}
                  href={`mailto:${guideContacts.email}`}
                />
              )}
            </Stack>
          )}
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          justifyContent: { xs: 'stretch', sm: 'flex-end' },
        }}
      >
        {booking.status === BookingStatus.PAYMENT_PENDING && onPay && (
          <Button
            variant="contained"
            size="small"
            disabled={payPending}
            onClick={() => onPay(booking.id)}
            sx={primaryBtnSx}
          >
            Pay now
          </Button>
        )}

        {booking.status === BookingStatus.EXPIRED && booking.guide?.profileId && (
          <Button
            component={RouterLink}
            to={`/guides/${booking.guide.profileId}`}
            variant="contained"
            size="small"
            sx={primaryBtnSx}
          >
            Book again
          </Button>
        )}

        {booking.status === BookingStatus.PAYMENT_PENDING && onCancel && (
          <Button
            variant="outlined"
            size="small"
            disabled={cancelPending}
            onClick={() => onCancel(booking.id)}
            sx={secondaryBtnSx}
          >
            Cancel
          </Button>
        )}

        {(booking.status === BookingStatus.CONFIRMED ||
          booking.status === BookingStatus.UPCOMING ||
          booking.status === BookingStatus.IN_PROGRESS ||
          booking.status === BookingStatus.COMPLETED) && (
          <>
            <Button
              component={RouterLink}
              to={`/bookings/${booking.id}/confirmation`}
              variant="outlined"
              size="small"
              sx={secondaryBtnSx}
            >
              Details
            </Button>
            {isExtendableStatus(booking.status) && (
              <Button
                component={RouterLink}
                to={`/bookings/${booking.id}/confirmation?extra=1`}
                variant="outlined"
                size="small"
                sx={secondaryBtnSx}
              >
                Add extra time
              </Button>
            )}
            {booking.status === BookingStatus.CONFIRMED && onCancel && (
              <Button
                variant="outlined"
                size="small"
                disabled={cancelPending}
                onClick={() => onCancel(booking.id)}
                sx={secondaryBtnSx}
              >
                Cancel
              </Button>
            )}
          </>
        )}

        {booking.status === BookingStatus.WAITING_FOR_TRAVELER_CONFIRMATION && (
          <>
            {onConfirmCompletion && (
              <Button
                variant="contained"
                size="small"
                disabled={confirmPending}
                onClick={() => onConfirmCompletion(booking.id)}
                sx={primaryBtnSx}
              >
                Confirm
              </Button>
            )}
            {onDispute && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => onDispute(booking)}
                sx={secondaryBtnSx}
              >
                Dispute
              </Button>
            )}
          </>
        )}

        {booking.status === BookingStatus.COMPLETED && onReview && (
          <Button
            variant="contained"
            size="small"
            onClick={() => onReview(booking)}
            sx={primaryBtnSx}
          >
            Leave review
          </Button>
        )}

        {(booking.status === BookingStatus.UPCOMING ||
          booking.status === BookingStatus.IN_PROGRESS) && (
          <>
            <Button
              component={RouterLink}
              to={`/bookings/${booking.id}/confirmation`}
              variant="contained"
              size="small"
              sx={primaryBtnSx}
            >
              View trip
            </Button>
            {onSos && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => onSos(booking)}
                sx={sosBtnSx}
              >
                SOS
              </Button>
            )}
          </>
        )}

        {booking.guide?.profileId && (
          <Button
            component={RouterLink}
            to={`/guides/${booking.guide.profileId}`}
            variant="outlined"
            size="small"
            sx={{
              ...secondaryBtnSx,
              borderColor: 'primary.main',
              color: 'primary.main',
              '&:hover': {
                borderColor: 'primary.dark',
                bgcolor: 'primary.50',
                color: 'primary.dark',
              },
            }}
          >
            View guide
          </Button>
        )}
      </Box>
    </Box>
  );
}

function MetaChip({ icon, label }: { icon: React.ReactElement; label: string }) {
  return (
    <Chip
      icon={icon}
      label={label}
      size="small"
      sx={{
        height: 28,
        fontSize: '0.75rem',
        fontWeight: 500,
        bgcolor: '#F8FAFC',
        color: 'text.secondary',
        border: '1px solid',
        borderColor: 'divider',
        '& .MuiChip-icon': { color: 'primary.main' },
      }}
    />
  );
}

function ContactChip({
  icon,
  label,
  href,
  external,
}: {
  icon: React.ReactElement;
  label: string;
  href: string;
  external?: boolean;
}) {
  return (
    <Chip
      component="a"
      clickable
      href={href}
      target={external ? '_blank' : undefined}
      rel={external ? 'noopener noreferrer' : undefined}
      icon={icon}
      label={label}
      size="small"
      sx={{
        height: 30,
        maxWidth: '100%',
        fontSize: '0.75rem',
        fontWeight: 500,
        bgcolor: 'primary.50',
        color: 'primary.dark',
        textDecoration: 'none',
        '& .MuiChip-icon': { color: 'primary.main' },
        '& .MuiChip-label': {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
        '&:hover': {
          bgcolor: 'primary.100',
        },
      }}
    />
  );
}

const primaryBtnSx = {
  bgcolor: 'primary.main',
  px: 2.25,
  py: 0.875,
  borderRadius: 2,
  fontSize: '0.8125rem',
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: 'none',
  minWidth: { xs: '100%', sm: 'auto' },
  '&:hover': {
    bgcolor: 'primary.dark',
    boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)',
  },
} as const;

const secondaryBtnSx = {
  borderColor: 'divider',
  color: 'text.secondary',
  px: 2,
  py: 0.875,
  borderRadius: 2,
  fontSize: '0.8125rem',
  fontWeight: 500,
  textTransform: 'none',
  minWidth: { xs: '100%', sm: 'auto' },
  '&:hover': {
    borderColor: 'primary.main',
    color: 'primary.main',
    bgcolor: 'primary.50',
  },
} as const;

const sosBtnSx = {
  borderColor: '#DC2626',
  color: '#DC2626',
  px: 2,
  py: 0.875,
  borderRadius: 2,
  fontSize: '0.8125rem',
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'none',
  minWidth: { xs: '100%', sm: 'auto' },
  '&:hover': {
    borderColor: '#B91C1C',
    color: '#B91C1C',
    bgcolor: '#FEE2E2',
  },
} as const;
